import React, { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "~/components/Button";
import { Input } from "~/components/Controls/Input";
import { SelectInput } from "~/components/Controls/Select";
import { MultiSelectPopup } from "~/components/Controls/Select/MultiSelectPopup";
import { useConfirm, useMessage } from "~/components/Dialogs";
import { IntentText } from "~/components/Form";
import { Progress } from "~/components/Progress";
import { TableRowActions, TableSort } from "~/components/Table";
import { Intent } from "~/lib/intent";
import { sortByString } from "~/lib/sort";
import { createPreset, deletePreset, updatePreset } from "~/modules/Dictionary/api";
import { formatRillFieldId, RILL_FIELD_GROUPS } from "~/modules/Dictionary/models/RillFieldCatalog";
import { PRESETS_QUERY_KEY, usePresetsQuery } from "~/modules/Dictionary/queries";
import { IPreset, PresetSide } from "~/modules/Dictionary/models/Preset";
import style from "./PresetsTab.module.scss";

const getErrorMessage = (error: any): string => error?.response?.data?.message || error?.message || "Unknown error";

const SIDE_ITEMS: { value: PresetSide; label: string }[] = [
  { value: "supply", label: "Supply" },
  { value: "demand", label: "Demand" },
];

type FormMode = "idle" | "create" | "edit";

const isDefaultPreset = (item: IPreset) => item.name.toLowerCase() === item.side;

/** Keeps only the fields the side's dashboards offer; any other id would be saved as a grant Rill ignores. */
const keepSideFields = (side: PresetSide, fieldIds: string[]): string[] => {
  const allowed = new Set(RILL_FIELD_GROUPS[side].flatMap((group) => group.items.map((item) => item.value)));
  return fieldIds.filter((fieldId) => allowed.has(fieldId));
};

export const PresetsTab = () => {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const message = useMessage();

  const { data: items = [], isPending, isFetching, isError, error, refetch } = usePresetsQuery();

  const [mode, setMode] = useState<FormMode>("idle");
  const [editingItem, setEditingItem] = useState<IPreset | null>(null);
  const [name, setName] = useState("");
  const [side, setSide] = useState<PresetSide>("supply");
  const [fieldIds, setFieldIds] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  const onSideChange = (next: PresetSide) => {
    setSide(next);
    setFieldIds((current) => keepSideFields(next, current));
  };

  const createMutation = useMutation({
    mutationFn: () =>
      createPreset({
        name: name.trim(),
        side,
        fieldIds,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PRESETS_QUERY_KEY });
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      updatePreset(editingItem!.side, editingItem!.id, {
        name: name.trim(),
        fieldIds,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PRESETS_QUERY_KEY });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (item: IPreset) => deletePreset(item.side, item.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PRESETS_QUERY_KEY });
    },
  });

  const normalizedItems = useMemo(() => items ?? [], [items]);
  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
  const isFormOpen = mode !== "idle";
  const isEditing = mode === "edit";
  const isEditingDefault = isEditing && editingItem != null && isDefaultPreset(editingItem);
  const canSubmit = name.trim().length > 0;

  const resetForm = () => {
    setFormError(null);
    setName("");
    setSide("supply");
    setFieldIds([]);
    setEditingItem(null);
    setMode("idle");
  };

  const handleOpenAdd = () => {
    setFormError(null);
    setName("");
    setSide("supply");
    setFieldIds([]);
    setEditingItem(null);
    setMode("create");
  };

  const handleEdit = (item: IPreset) => {
    setFormError(null);
    setName(item.name);
    setSide(item.side);
    setFieldIds(keepSideFields(item.side, item.fieldIds));
    setEditingItem(item);
    setMode("edit");
  };

  const handleCancel = () => resetForm();

  const handleSave = () => {
    setFormError(null);
    const mutation = isEditing ? updateMutation : createMutation;
    mutation.mutate(undefined, {
      onError: (e: any) => {
        const msg = getErrorMessage(e);
        setFormError(msg);
        message(msg, { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
      },
    });
  };

  const handleDelete = (item: IPreset) =>
    confirm(`Delete preset "${item.name}"?`, {
      icon: <i className="fa fa-question" />,
      intent: Intent.DANGER,
      onOk: () => {
        deleteMutation.mutate(item, {
          onError: (e: any) => {
            const status = e?.response?.status;
            if (status === 409) {
              message("This preset is assigned to one or more Sellers/Agencies and cannot be deleted.", {
                intent: Intent.DANGER,
                icon: <i className="fa fa-times" />,
              });
              return;
            }
            if (status === 404) {
              message("Not found.", { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
              return;
            }
            message(getErrorMessage(e), { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
          },
        });
      },
    });

  return (
    <div>
      <div className={style.toolbar}>
        <div className={style.toolbarLeft}>
          {isFormOpen && (
            <div className={style.formRow}>
              <Input
                className={style.field}
                placeholder="Name"
                value={name}
                onChange={setName}
                disabled={isBusy || isEditingDefault}
                autoFocus
              />
              <SelectInput
                className={style.field}
                items={SIDE_ITEMS}
                value={side}
                onChange={onSideChange}
                disabled={isBusy || isEditing}
              />
              <div className={[style.field, style.fieldIds].join(" ")}>
                <MultiSelectPopup
                  groups={RILL_FIELD_GROUPS[side]}
                  value={fieldIds}
                  onChange={setFieldIds}
                  title="Select field IDs"
                  placeholder="Select field IDs"
                  disabled={isBusy}
                />
              </div>
            </div>
          )}
        </div>
        <div className={style.toolbarRight}>
          {!isFormOpen ? (
            <Button
              responsive
              intent={Intent.PRIMARY}
              disabled={isBusy}
              onClick={handleOpenAdd}
              leftIcon={<i className="fa fa-plus" />}
            >
              Add
            </Button>
          ) : (
            <>
              <Button
                responsive
                intent={Intent.PRIMARY}
                disabled={!canSubmit || isBusy}
                onClick={handleSave}
                leftIcon={<i className="fa fa-check" />}
              >
                {isEditing ? "Update" : "Save"}
              </Button>
              <Button
                responsive
                disabled={isBusy}
                onClick={handleCancel}
                leftIcon={<i className="fa fa-times" />}
              >
                Cancel
              </Button>
            </>
          )}
          <Button
            responsive
            intent={Intent.SUCCESS}
            disabled={isFetching}
            onClick={() => refetch()}
            leftIcon={<i className="fa fa-refresh" />}
          >
            Reload
          </Button>
        </div>
      </div>

      {isFormOpen && formError && <IntentText className={style.error} intent={Intent.DANGER}>{formError}</IntentText>}
      {isError && <IntentText className={style.error} intent={Intent.DANGER}>{getErrorMessage(error)}</IntentText>}

      <div className={style.tableWrap}>
        {isPending && normalizedItems.length === 0 && <Progress message="Loading..." />}
        {!isPending && normalizedItems.length === 0 && (
          <div className={style.emptyState}>No presets yet.</div>
        )}
        {normalizedItems.length > 0 && (
          <TableSort
            records={normalizedItems}
            header
            columns={[
              { header: "Name", field: "name", sort: sortByString },
              { header: "Side", field: "side", sort: sortByString },
              {
                header: "Field IDs",
                cell: (item: IPreset) => item.fieldIds.map(formatRillFieldId).join(", ") || "—",
                cellWrap: "clamp-2",
              },
              {
                short: true,
                cell: (item: IPreset) => (
                  <TableRowActions>
                    <Button
                      small
                      variant="menu"
                      intent={Intent.PRIMARY}
                      disabled={isBusy}
                      onClick={() => handleEdit(item)}
                      leftIcon={<i className="fa fa-edit" />}
                    >
                      Edit
                    </Button>
                    {!isDefaultPreset(item) && (
                      <Button
                        small
                        variant="menu"
                        intent={Intent.DANGER}
                        disabled={isBusy}
                        onClick={() => handleDelete(item)}
                        leftIcon={<i className="fa fa-trash" />}
                      >
                        Delete
                      </Button>
                    )}
                  </TableRowActions>
                ),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
};
